import CategoryFilterCard from 'components/cards/CategoryFilterCard'
import React, { useEffect } from 'react'
import styles from 'styles/layout/SelectedReportCategoryBodySection.module.sass'
import { useAuth } from 'hooks/AuthContext'
import bagIcon from 'assets/images/svg/bag-dynamic.svg';
import PrimaryButton from 'components/common/PrimaryButton'
import { useNavigate } from 'react-router-dom'
import ReportCategoryDisplaySection from './ReportCategoryDisplaySection';
import { CategoryTopic } from 'interface/CategoryTopic';

/**
 * Props for the SelectedReportCategoryBodySection component.
 *
 * @param id - The ID of the selected report category.
 * @param allReports - All available reports.
 */
interface SelectedReportCategoryBodySectionProps {
    id: string;
    allReports: CategoryTopic[]
}

/**
 * SelectedReportCategoryBodySection component displays the body section for a selected report category.
 *
 * @param id - The ID of the selected report category.
 * @param allReports - All available reports.
 *
 * @returns The rendered SelectedReportCategoryBodySection component.
 * 
 * @example
 * <SelectedReportCategoryBodySection id="7a806589-677a-44d3-a3c8-4648fbfa5edc" allReports={allReports} />
 */
const SelectedReportCategoryBodySection: React.FC<SelectedReportCategoryBodySectionProps> = ({ id, allReports }) => {
    const { userSubscription } = useAuth()
    const navigate = useNavigate();
    const isSubscribed = userSubscription && userSubscription.name.toLocaleLowerCase() !== 'free';
    const [filteredReports, setFilteredReports] = React.useState<CategoryTopic[]>(allReports);
    
    useEffect(() => {
        setFilteredReports(allReports);
    }, [allReports]);

    return (
        <section className={styles['selected-report-category-body-section']}>
            <div className={styles['left-column']}>
                <CategoryFilterCard
                    id={id}
                    reports={allReports}
                    setFilteredReports={setFilteredReports}
                />
                {!isSubscribed && (
                    <div className={styles['unlimited-report-access-card']}>
                        <img src={bagIcon} alt="Unlimited report access icon" className={styles['bag-icon']} />
                        <div className={styles['text-wrapper']}>
                            <p className={styles['main-title']}>Unlimited Access</p>
                            <p className={styles['description-text']}>
                                Get all these reports at your fingertips with one of our <strong>Business</strong> or <strong>Individual</strong> Plans.
                            </p>
                        </div>
                        <PrimaryButton 
                            text="View Plans"
                            primaryColor="#ff5522"
                            textColor="#fff"
                            hoverColor="#fff"
                            hoverTextColor="#ff5522"
                            width="100%"
                            doSomething={() => navigate('/pricing')}
                        />
                    </div>
                )}
            </div>
            <div className={styles['right-column']}>
                <ReportCategoryDisplaySection allReports={allReports} filteredReports={filteredReports} setFilteredReports={setFilteredReports} />
            </div>
        </section>
    )
}

export default SelectedReportCategoryBodySection